// SiteOverviewDashboard — card-based dashboard landing pane (djin-cloud#67).
//
// Phases 1–6 all shipped (complete):
//   - Phase 1 (PR #106): flag-gated shell.
//   - Phase 2 (PR #107): SystemSummaryCard + AlarmSummaryCard.
//   - Phase 3 (PR #108): BatteryStorageCard + SiteMeterCard + site_summary
//     extensions (site_power_factor, bess counts, meter_* fields).
//   - Phase 4 (PR #111): GeneratorsCard + site_summary.generators[] fan-out
//     (generators_online_count, generators_total_count, generators[]).
//   - Phase 5 (PR #112): ChartCard with curated swappable presets in row 2.
//   - Phase 6: flag flip + orphan OverviewTab cleanup; dashboard is now the
//     unconditional Site Overview landing pane.
//
// Layout (desktop grid-cols-12):
//   row 1 · System Summary           (span 12) — Phase 2 ✓
//   row 2 · Chart | Alarms           (8 | 4)   — Phase 5 / Phase 2 ✓
//   row 3 · Meter | Battery          (6 | 6)   — Phase 3 ✓ (reordered #273)
//          Battery is gated on site.asset_types.includes('battery_storage').
//          When gated off, Meter expands to full width via grid reflow.
//   row 4 · Generators               (span 12) — Phase 4 ✓ (reordered #273)
//          Gated on site.asset_types.includes('generator') (#889), mirroring
//          the Battery gate above; a generator-less site renders no row 4.
//   The Market Price card (#802, row 5) was removed with djin-cloud#1089: a
//   site's market now has its own routed Market tab, gated on
//   site.market_descriptor.

import type { SiteConfig, AlarmSummary, KPIData, GeneratorRow } from '../../../types/project.types';
import { SystemSummaryCard } from '../cards/SystemSummaryCard';
import { AlarmSummaryCard } from '../cards/AlarmSummaryCard';
import { BatteryStorageCard } from '../cards/BatteryStorageCard';
import { SiteMeterCard } from '../cards/SiteMeterCard';
import { GeneratorsCard } from '../cards/GeneratorsCard';
import { ChartCard } from '../cards/ChartCard';

interface SiteOverviewDashboardProps {
  siteId: string;
  site: SiteConfig;
  alarmSummary: AlarmSummary;
  /** Live site-level KPI values from `telemetry:batch`'s `site_summary` payload. */
  kpiValues: Record<string, number | null>;
  /** Cold-load asset KPIs from `/api/v1/projects/:id` `data.kpis`. */
  kpis: KPIData[];
  /**
   * Per-generator fan-out from `site_summary.generators` (WebSocket wire).
   * `null` = unknown roster; `[]` = known-zero or roster>0 awaiting data.
   * Kept separate from `kpiValues` (which is `Record<string, number | null>`)
   * to preserve array typing.
   */
  generators: GeneratorRow[] | null;
}

export default function SiteOverviewDashboard({
  siteId,
  site,
  alarmSummary,
  kpiValues,
  kpis,
  generators,
}: SiteOverviewDashboardProps) {
  const hasBattery = Array.isArray(site.asset_types) && site.asset_types.includes('battery_storage');
  // Same gate shape as hasBattery, for the same reason (djin-cloud#889). A site
  // with no generators rendered a full-width "No generators at this site" panel
  // — a whole row spent saying nothing. Station-158 is ["battery_storage","meter"].
  // Gated on asset_types, NOT on the live `generators` array: [] legitimately
  // means "roster has generators, none reported in this batch", and hiding the
  // card then would make real generators vanish on a thin batch.
  const hasGenerators = Array.isArray(site.asset_types) && site.asset_types.includes('generator');
  const nameplateCapacityMwh =
    (site.kpi_metadata?.battery_storage?.nameplate_capacity_mwh as number | undefined) ?? null;

  return (
    <div className="p-6 space-y-4">
      <SystemSummaryCard kpiValues={kpiValues} />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8">
          <ChartCard siteId={siteId} site={site} />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <AlarmSummaryCard siteId={siteId} alarmSummary={alarmSummary} />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className={hasBattery ? 'col-span-12 lg:col-span-6' : 'col-span-12 lg:col-span-12'}>
          <SiteMeterCard kpiValues={kpiValues} />
        </div>
        {hasBattery && (
          <div className="col-span-12 lg:col-span-6">
            <BatteryStorageCard
              kpis={kpis}
              nameplateCapacityMwh={nameplateCapacityMwh}
              kpiValues={kpiValues}
            />
          </div>
        )}
      </div>

      {hasGenerators && <GeneratorsCard kpiValues={kpiValues} generators={generators} />}
    </div>
  );
}

// (Phase 5: Placeholder helper removed — ChartCard fills row 2 left slot.
// Phase 6: orphan OverviewTab removed; dashboard is unconditional.)
